/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { memo, useCallback, useState } from 'react';
import { useRegisterNudgeTarget } from '@atlassiansox/nudge-tooltip';
import { useLocation } from '@jira/navigation-apps__routing-hooks/src/useLocation.tsx';
import { InviteTeamNudgeAsync } from '@jira/software__onboarding-nudges/src/InviteTeamNudgeAsync.tsx';
import AddIcon from '@atlaskit/icon/core/add';
import IconButton from '@atlaskit/button/icon/button';
import ButtonItem from '@atlaskit/menu/button-item';
import MenuGroup from '@atlaskit/menu/menu-group';
import Stack from '@atlaskit/primitives/stack';
import { xcss } from '@atlaskit/primitives/xcss';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';
import { mergeRefs } from '@atlassian/jira-merge-refs';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { BUTTON } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/action-subject-type.tsx';
import {
	FIXED_ITEM_DROPDOWN_ACTION,
	FIXED_ITEM_BUTTON,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import {
	LEVEL_TWO,
	LEVEL_ONE,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/level.tsx';
import { SECTION_ITEM_TEAMS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { MENU_ID_ANALYTIC_EVENT_IDS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/side-nav.tsx';
import { MENU_ID_TEAMS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import {
	L1_MENU_ID,
	type Nav4DragAndDropMenuItemProps,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { useSectionItemAnalytics } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-section-item-analytics/index.tsx';
import { useConfigurableMessageAndIconMap } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
import { HideL1MenuItemSection } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/hide-l1-menu-item-action/index.tsx';
import { MoreNavMenuButton } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/more-nav-menu-button/main.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { JWM_INVITE_TEAM_NUDGE_ID } from '@jira/platform__onboarding-core/src/constants.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	CREATE_A_TEAM,
	INVITE_PEOPLE,
	MEATBALL_DROPDOWN,
	PEOPLE_SEARCH_HREF,
	PLUS_BUTTON,
} from '../common/constants.tsx';
import { useCreateTeamModal } from './use-create-team-modal/index.tsx';
import { useInvitePeopleModal } from './use-invite-people-modal.tsx';

const teamsAddButtonMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-teams.common.action-buttons.add-button',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Create or invite',
	description: 'Button to create a new team or invite new user',
});

const teamsCreateTeamMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-teams.common.action-buttons.create-team',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Create a team',
	description: 'Used in popup menu for user to create a team',
});

const teamsInvitePeopleMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-teams.common.action-buttons.invite-people',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Invite people',
	description: 'Used in popup menu for user to invite new user',
});

interface Props extends Nav4DragAndDropMenuItemProps {}

export const TeamsMenu: React.MemoExoticComponent<(props: Props) => React.JSX.Element> = memo(
	(props: Props): React.JSX.Element => {
		const { draggableRef, dropIndicator, hasDragIndicator, isDragging, visualContentRef } = props;
		const [isOpenTeamsPopup, openTeamsPopup] = useState(false);
		const { formatMessage } = useIntl();
		const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.TEAMS);

		const location = useLocation();
		const path = location.pathname + location.search;
		const isSelected = path === PEOPLE_SEARCH_HREF;

		const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const actionSubjectId = MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_TEAMS];
		const label = formatMessage(teamsAddButtonMessage);

		const [createTeamModal, createTeamModalTrigger] = useCreateTeamModal();

		const onLinkClick = useCallback(() => {
			sendSectionItemAnalyticsEvent({
				menuId: L1_MENU_ID.TEAMS,
				sectionItem: SECTION_ITEM_TEAMS,
			});
		}, [sendSectionItemAnalyticsEvent]);

		const onPlusButtonClick = useCallback(() => {
			fireUIAnalytics(createAnalyticsEvent({}), {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId,
				attributes: {
					level: LEVEL_ONE,
					componentType: FIXED_ITEM_BUTTON,
					parentItemId: SECTION_ITEM_TEAMS,
					itemId: PLUS_BUTTON,
				},
			});
			openTeamsPopup(true);
		}, [createAnalyticsEvent, actionSubjectId]);

		const peopleMenuWrapperRef =
			useRegisterNudgeTarget<HTMLAnchorElement>(JWM_INVITE_TEAM_NUDGE_ID).ref;

		const renderContent = useCallback(() => {
			return (
				<PopupContent
					openTeamsPopup={openTeamsPopup}
					createTeamModalTrigger={createTeamModalTrigger}
				/>
			);
		}, [createTeamModalTrigger, openTeamsPopup]);

		const onMoreNavMenuButtonClick = useCallback(() => {
			fireUIAnalytics(createAnalyticsEvent({}), {
				action: 'clicked',
				actionSubject: BUTTON,
				actionSubjectId,
				attributes: {
					level: LEVEL_ONE,
					componentType: FIXED_ITEM_BUTTON,
					itemId: MEATBALL_DROPDOWN,
				},
			});
		}, [actionSubjectId, createAnalyticsEvent]);

		return (
			<>
				{createTeamModal}
				<InviteTeamNudgeAsync />
				<LinkMenuItem
					ref={mergeRefs(
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						draggableRef!,
						peopleMenuWrapperRef,
					)}
					// eslint-disable-next-line jira/integration/test-id-by-folder-structure
					testId="navigation-apps-sidebar-nav4-sidebars-content-teams.ui.sidenav-teams"
					hasDragIndicator={hasDragIndicator}
					isDragging={isDragging}
					dropIndicator={dropIndicator}
					visualContentRef={visualContentRef}
					href={PEOPLE_SEARCH_HREF}
					isSelected={isSelected}
					elemBefore={icon}
					onClick={onLinkClick}
					actionsOnHover={
						<>
							<Popup
								shouldRenderToParent
								placement="bottom-start"
								// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
								messageId="navigation-apps-sidebar-nav4-sidebars-content-teams.ui.popup"
								messageType="transactional"
								onClose={useCallback(() => openTeamsPopup(false), [])}
								trigger={(triggerProps) => (
									<IconButton
										{...triggerProps}
										icon={(iconProps) => <AddIcon {...iconProps} size="small" />}
										appearance="subtle"
										spacing="compact"
										// eslint-disable-next-line jira/integration/test-id-by-folder-structure
										testId="navigation-apps-sidebar-nav4-sidebars-content-teams.ui.sidenav-teams-popup-trigger"
										isSelected={isOpenTeamsPopup}
										label={label}
										tooltip={{ content: label }}
										isTooltipDisabled={false}
										onClick={onPlusButtonClick}
									/>
								)}
								isOpen={isOpenTeamsPopup}
								content={renderContent}
							/>
							{!fg('navx-global-side-nav') && (
								<MoreNavMenuButton onOpen={onMoreNavMenuButtonClick} MenuItems={MenuItems} />
							)}
						</>
					}
				>
					{formattedMessage}
				</LinkMenuItem>
			</>
		);
	},
);

const PopupContent = ({
	openTeamsPopup,
	createTeamModalTrigger,
}: {
	openTeamsPopup: (isOpen: boolean) => void;
	createTeamModalTrigger: () => void;
}) => {
	const invitePeopleModalTrigger = useInvitePeopleModal();

	const actionSubjectId = MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_TEAMS];

	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onButtonClick = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: 'dropdownItem',
			actionSubjectId,
			attributes: {
				level: LEVEL_TWO,
				componentType: FIXED_ITEM_DROPDOWN_ACTION,
				parentItemId: SECTION_ITEM_TEAMS,
				itemId: INVITE_PEOPLE,
			},
		});
		invitePeopleModalTrigger();
		openTeamsPopup(false);
	}, [createAnalyticsEvent, actionSubjectId, invitePeopleModalTrigger, openTeamsPopup]);
	const onCreateTeamClick = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: 'dropdownItem',
			actionSubjectId,
			attributes: {
				level: LEVEL_TWO,
				componentType: FIXED_ITEM_DROPDOWN_ACTION,
				parentItemId: SECTION_ITEM_TEAMS,
				itemId: CREATE_A_TEAM,
			},
		});
		createTeamModalTrigger();
		openTeamsPopup(false);
	}, [createAnalyticsEvent, actionSubjectId, createTeamModalTrigger, openTeamsPopup]);
	return (
		<Stack xcss={teamsMenuStyle}>
			<ButtonItem onClick={onButtonClick}>{formatMessage(teamsInvitePeopleMessage)}</ButtonItem>
			<ButtonItem onClick={onCreateTeamClick}>{formatMessage(teamsCreateTeamMessage)}</ButtonItem>
		</Stack>
	);
};

const MenuItems = () => (
	<MenuGroup>
		<HideL1MenuItemSection menuId={MENU_ID_TEAMS} hasSeparator={false} />
	</MenuGroup>
);

const teamsMenuStyle = xcss({
	minWidth: '260px',
	paddingBlock: 'space.100',
});
